/**
 * Table export in the three formats the product promises: CSV, Excel and PDF
 * (FEAT-2026-015). Everything exports the data already on screen — no backend
 * round trip, so an export always matches what the admin is looking at.
 *
 * Callers describe the table once and pick a format, rather than hand-rolling
 * a string per page as before.
 *
 * Format notes:
 *  - **CSV** — RFC 4180 quoting, UTF-8 BOM so Excel does not mangle accents.
 *  - **XLSX** — real cell types: numbers stay numeric and can be summed in
 *    Excel, which CSV cannot guarantee. SheetJS is loaded on demand so it
 *    stays out of the initial bundle.
 *  - **PDF** — rendered through the browser's own print pipeline (hidden
 *    iframe → "Save as PDF"). That keeps pagination, repeated table headers
 *    and font rendering native, and adds no dependency. It opens the print
 *    dialog rather than downloading silently; a true one-click PDF would mean
 *    adding a PDF library.
 */

export type Cell = string | number | null | undefined;

export interface ExportTable {
  /** Base filename, no extension. */
  filename: string;
  /** Shown as the PDF heading and the worksheet name. */
  title: string;
  headers: string[];
  rows: Cell[][];
  /** Context printed under the PDF title, e.g. date range and active filters. */
  meta?: Record<string, string>;
}

const text = (v: Cell): string => (v == null ? "" : String(v));

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * The table without the columns named in `hidden` (FEAT-2026-029): HR picks
 * which columns an export carries. A name that is no longer a column is
 * ignored, so a choice saved before a rename doesn't break the export.
 */
export function withoutColumns(table: ExportTable, hidden: string[]): ExportTable {
  const keep = table.headers.map((h, i) => (hidden.includes(h) ? -1 : i)).filter((i) => i >= 0);
  if (keep.length === table.headers.length) return table;
  return {
    ...table,
    headers: keep.map((i) => table.headers[i]),
    rows: table.rows.map((r) => keep.map((i) => r[i])),
  };
}

/** RFC 4180: quote anything containing a comma, quote or newline. */
export function toCsv(headers: string[], rows: Cell[][]): string {
  const esc = (v: Cell): string => {
    const s = text(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [headers, ...rows].map((row) => row.map(esc).join(",")).join("\r\n");
}

export function downloadCsv(table: ExportTable): void {
  // Leading BOM: without it Excel reads UTF-8 as Latin-1 and mangles accents.
  const blob = new Blob(["\ufeff", toCsv(table.headers, table.rows)], {
    type: "text/csv;charset=utf-8",
  });
  triggerDownload(blob, `${table.filename}.csv`);
}

export async function downloadXlsx(table: ExportTable): Promise<void> {
  const XLSX = await import("xlsx"); // heavy; only when actually exporting
  const sheet = XLSX.utils.aoa_to_sheet([
    table.headers,
    // Pass numbers through unchanged so they land as numeric cells.
    ...table.rows.map((r) => r.map((c) => (c == null ? "" : c))),
  ]);
  // Width each column to its widest value, within sane bounds.
  sheet["!cols"] = table.headers.map((h, i) => {
    const longest = Math.max(h.length, ...table.rows.map((r) => text(r[i]).length));
    return { wch: Math.min(Math.max(longest + 2, 10), 50) };
  });
  const book = XLSX.utils.book_new();
  // Excel rejects sheet names over 31 chars or containing []:*?/\
  const safeName = table.title.replace(/[[\]:*?/\\]/g, " ").slice(0, 31) || "Sheet1";
  XLSX.utils.book_append_sheet(book, sheet, safeName);
  XLSX.writeFile(book, `${table.filename}.xlsx`);
}

const escapeHtml = (s: string): string =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/**
 * Render the table into a hidden iframe and open the print dialog. An iframe
 * rather than `window.open` so pop-up blockers do not eat it.
 */
export function printPdf(table: ExportTable): void {
  const meta = Object.entries(table.meta ?? {})
    .map(([k, v]) => `<span><b>${escapeHtml(k)}:</b> ${escapeHtml(v)}</span>`)
    .join("");

  const html = `<!doctype html><html><head><meta charset="utf-8">
<title>${escapeHtml(table.filename)}</title><style>
  @page { size: A4 landscape; margin: 14mm; }
  * { font-family: -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
  body { margin: 0; color: #111; }
  h1 { font-size: 16pt; margin: 0 0 2mm; }
  .meta { font-size: 9pt; color: #555; margin-bottom: 5mm; display: flex; gap: 6mm; flex-wrap: wrap; }
  table { border-collapse: collapse; width: 100%; font-size: 9pt; }
  thead { display: table-header-group; }           /* repeat header on each page */
  tr { break-inside: avoid; }
  th, td { border: 1px solid #d0d0d0; padding: 3px 6px; text-align: left; vertical-align: top; }
  th { background: #f2f4f7; font-weight: 600; }
  tbody tr:nth-child(even) { background: #fafbfc; }
  .num { text-align: right; font-variant-numeric: tabular-nums; }
</style></head><body>
  <h1>${escapeHtml(table.title)}</h1>
  <div class="meta">${meta}</div>
  <table><thead><tr>${table.headers.map((h) => `<th>${escapeHtml(h)}</th>`).join("")}</tr></thead>
  <tbody>${table.rows
    .map(
      (r) =>
        `<tr>${r
          .map((c) => `<td class="${typeof c === "number" ? "num" : ""}">${escapeHtml(text(c))}</td>`)
          .join("")}</tr>`,
    )
    .join("")}</tbody></table>
</body></html>`;

  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  document.body.appendChild(frame);
  const doc = frame.contentDocument;
  if (!doc) {
    frame.remove();
    throw new Error("Could not open the print view");
  }
  doc.open();
  doc.write(html);
  doc.close();
  const run = () => {
    frame.contentWindow?.focus();
    frame.contentWindow?.print();
    // Leave it long enough for the dialog to take its snapshot.
    setTimeout(() => frame.remove(), 60_000);
  };
  if (doc.readyState === "complete") run();
  else frame.onload = run;
}

export type ExportFormat = "csv" | "xlsx" | "pdf";

export async function exportTable(table: ExportTable, format: ExportFormat): Promise<void> {
  if (format === "csv") return downloadCsv(table);
  if (format === "xlsx") return downloadXlsx(table);
  return printPdf(table);
}
